import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Users } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import MonthPicker from '../components/MonthPicker';
import { MyPeopleData } from '../components/home/types';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { usePeriodParams } from '../hooks/usePeriodParams';
import { navLabel } from '../utils/navigation';
import { canAccessPath, roleOf } from '../utils/roles';
import { personPath } from '../components/home/sentences';
import * as fmt from '../lib/format';

const CARD = 'rounded-2xl bg-white shadow-sm ring-1 ring-gray-200/70 contrast-more:ring-gray-400';
const TH = 'px-3 py-2 text-left text-[0.6875rem] font-semibold uppercase tracking-wider text-gray-500';
const TD = 'whitespace-nowrap px-3 py-3 text-sm tabular-nums text-gray-900';

// My people: the people in the reporting lines the signed-in login manages or is assigned to, with the month's numbers the
// pages behind them show. A leader or an admin may look at any team. The account is the login's own; the page never names one.
export default function MyPeoplePage() {
  const { user } = useAuth();
  const role = roleOf(user);
  const { period, current, setPeriod, params, update } = usePeriodParams();
  const teamParam = Number(params.get('team_id'));
  const teamId = role !== 'user' && Number.isInteger(teamParam) && teamParam > 0 ? teamParam : null;

  const [data, setData] = useState<MyPeopleData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiService.getMyPeople(period, teamId ?? undefined);
      if (!r.success) throw new Error(r.error);
      setData(r.data);
      setError(null);
    } catch (e: any) {
      setData(null);
      setError(e?.response?.data?.error || e?.message || 'Could not read your people');
    } finally {
      setLoading(false);
    }
  }, [period, teamId]);

  useEffect(() => {
    void load();
  }, [load]);

  const canOpenMonth = canAccessPath(role, '/reports');
  const showFix = !!data && data.people.some((p) => p.to_fix !== null);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100/60">
      <PageHeader title={navLabel('/my-people')} subtitle="The people you work with, and how their month is going" icon={Users} />
      <div className="p-4 sm:p-6">
        <div className="mx-auto max-w-6xl">
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <MonthPicker value={period} max={current} onChange={setPeriod} className="" />
            {role !== 'user' && data && data.teams.length > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                <label htmlFor="people-team" className="text-sm text-gray-700">
                  Team
                </label>
                <select
                  id="people-team"
                  value={teamId ?? ''}
                  onChange={(e) => update({ team_id: e.target.value || null })}
                  className="max-w-full rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                >
                  <option value="">My teams</option>
                  {data.teams.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {error && (
            <div role="alert" className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-rose-50 p-4 text-sm text-rose-800 ring-1 ring-rose-200">
              <span>{error}</span>
              <button type="button" onClick={() => void load()} className="rounded-lg border border-rose-300 bg-white px-3 py-1.5 font-medium text-rose-800 hover:bg-rose-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-500">
                Try again
              </button>
            </div>
          )}
          {loading && !data && !error && <div className={`${CARD} p-8 text-center text-sm text-gray-600`}>Reading your people…</div>}

          {data && data.people.length === 0 && (
            <div className={`${CARD} p-6 text-sm text-gray-700`}>
              {data.no_team && teamId === null
                ? 'You are not on a team yet, so there is nobody to show. Ask an admin to add you to one in Settings → Manage teams.'
                : 'Nobody is in this team yet.'}
            </div>
          )}

          {data && data.people.length > 0 && (
            <div className={`${CARD} relative overflow-x-auto`}>
              <table className="min-w-full divide-y divide-gray-100">
                <caption className="sr-only">People and their month</caption>
                <thead>
                  <tr>
                    <th scope="col" className={`${TH} sticky left-0 bg-white`}>
                      Person
                    </th>
                    <th scope="col" className={`${TH} text-right`}>
                      Hours
                    </th>
                    <th scope="col" className={`${TH} text-right`}>
                      Shipped
                    </th>
                    <th scope="col" className={`${TH} text-right`}>
                      In the sprint
                    </th>
                    <th scope="col" className={`${TH} text-right`}>
                      Waiting
                    </th>
                    {showFix && (
                      <th scope="col" className={`${TH} text-right`}>
                        To fix
                      </th>
                    )}
                    {canOpenMonth && (
                      <th scope="col" className={TH}>
                        <span className="sr-only">Open</span>
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data.people.map((p) => (
                    <tr key={p.account_id}>
                      <th scope="row" className="sticky left-0 bg-white px-3 py-3 text-left text-sm font-medium text-gray-900">
                        {p.display_name ?? p.account_id}
                      </th>
                      <td className={`${TD} text-right`}>{p.hours === null ? <span aria-hidden>–</span> : fmt.hours(p.hours)}</td>
                      <td className={`${TD} text-right`}>{fmt.count(p.shipped)}</td>
                      <td className={`${TD} text-right`}>{fmt.count(p.open_in_sprint)}</td>
                      <td className={`${TD} text-right`}>{fmt.count(p.waiting)}</td>
                      {showFix && <td className={`${TD} text-right`}>{p.to_fix === null ? <span aria-hidden>–</span> : fmt.count(p.to_fix)}</td>}
                      {canOpenMonth && (
                        <td className="px-3 py-3 text-sm">
                          <Link to={personPath(p.account_id)} className="rounded px-1 py-1 text-xs font-medium text-blue-700 underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">
                            {`Open ${p.display_name ?? 'their'} month`}
                          </Link>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {data && data.people.length > 0 && (
            <p className="mt-3 text-sm text-gray-600">
              Hours are the Worklogs by person grid&apos;s, shipped is what moved into Released this month, and waiting is what has not moved for longer than its status allows.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
